package com.speedsoftware.nibbler.models

import kotlinx.serialization.Serializable

@Serializable
enum class UserRole {
    SUPER_ADMIN,
    USER
}

@Serializable
enum class ProjectRole {
    ADMIN,
    DEVELOPER
}

@Serializable
enum class IssueType(val displayName: String, val code: String) {
    BUG("Bug", "BUG"),
    FEATURE("Feature", "FEAT"),
    TASK("Task", "TASK"),
    IMPROVEMENT("Improvement", "IMP"),
    STORY("Story", "STORY")
}

/** Default board columns created for every new board (name to accent color). */
val DEFAULT_BOARD_COLUMNS: List<Pair<String, String>> = listOf(
    "To do" to "#7AA7F7",
    "In progress" to "#F5B544",
    "In review" to "#8B7CF6",
    "Done" to "#34D399"
)

@Serializable
enum class TaskPriority(val displayName: String, val level: Int) {
    LOW("Low", 1),
    MEDIUM("Medium", 2),
    HIGH("High", 3),
    URGENT("Urgent", 4)
}

@Serializable
enum class SprintStatus {
    PLANNED,
    ACTIVE,
    COMPLETED
}

@Serializable
data class SprintDto(
    val id: String,
    val projectId: String,
    val name: String,
    val goal: String = "",
    val startDate: String = "",
    val endDate: String = "",
    val status: SprintStatus = SprintStatus.PLANNED,
    val createdAt: String
)

@Serializable
data class CreateSprintRequest(
    val name: String,
    val goal: String = "",
    val startDate: String = "",
    val endDate: String = "",
    val status: SprintStatus = SprintStatus.PLANNED
)

@Serializable
data class UpdateSprintRequest(
    val name: String? = null,
    val goal: String? = null,
    val startDate: String? = null,
    val endDate: String? = null,
    val status: SprintStatus? = null
)

@Serializable
data class UserDto(
    val id: String,
    val email: String,
    val name: String,
    val role: UserRole,
    val createdAt: String,
    val avatarUrl: String? = null,
    val avatarEmoji: String? = null,
    val twoFactorEnabled: Boolean = false
)

/** Empty string clears a field; null leaves it unchanged. */
@Serializable
data class UpdateProfileRequest(
    val name: String? = null,
    val avatarUrl: String? = null,
    val avatarEmoji: String? = null
)

@Serializable
data class AuthRequest(
    val email: String,
    val password: String
)

/**
 * Result of the password step. Either [token] and [user] are set (logged in), or [twoFactorRequired] is true
 * and the [challengeToken] must be sent to the 2FA endpoint together with a code.
 */
@Serializable
data class LoginResponse(
    val token: String? = null,
    val user: UserDto? = null,
    val twoFactorRequired: Boolean = false,
    val challengeToken: String? = null
)

/** A login session ("device") of the current user. Times are relative so clock differences do not matter. */
@Serializable
data class SessionDto(
    val id: String,
    /** Browser / app identification as sent at login; may be missing. */
    val userAgent: String? = null,
    val createdAgoSeconds: Long,
    val lastSeenAgoSeconds: Long,
    val expiresInSeconds: Long,
    /** True for the session that made this request. */
    val current: Boolean = false
)

@Serializable
data class TwoFactorLoginRequest(
    val challengeToken: String,
    /** Six-digit authenticator code, or a one-time recovery code. */
    val code: String
)

@Serializable
data class TwoFactorSetupResponse(
    /** Base32 secret, for manual entry. */
    val secret: String,
    /** otpauth:// URI that the QR code encodes. */
    val otpauthUri: String
)

@Serializable
data class TwoFactorCodeRequest(val code: String)

@Serializable
data class TwoFactorDisableRequest(
    val password: String,
    val code: String
)

/** Recovery codes are shown exactly once, right after they are generated. */
@Serializable
data class RecoveryCodesResponse(val recoveryCodes: List<String>)

@Serializable
data class AuthResponse(
    val token: String,
    val user: UserDto
)

@Serializable
data class CreateInviteRequest(
    val email: String,
    val role: UserRole = UserRole.USER
)

@Serializable
data class InviteDto(
    val id: String,
    val email: String,
    val role: UserRole,
    val token: String,
    val createdAt: String,
    val expiresAt: String,
    val accepted: Boolean
)

@Serializable
data class AcceptInviteRequest(
    val token: String,
    val name: String,
    val password: String
)

@Serializable
data class ProjectDto(
    val id: String,
    val key: String, // e.g. "NIB"
    val name: String,
    val description: String = "",
    val imageUrl: String? = null,
    /** Summary numbers for the project list; the detail endpoint fills the full boards and members instead. */
    val issueCount: Int = 0,
    val doneCount: Int = 0,
    val memberCount: Int = 0,
    val createdAt: String,
    val members: List<ProjectMemberDto> = emptyList(),
    val boards: List<BoardDto> = emptyList(),
    val sprints: List<SprintDto> = emptyList()
)

@Serializable
data class CreateProjectRequest(
    val key: String,
    val name: String,
    val description: String = "",
    val imageUrl: String? = null
)

@Serializable
data class UpdateProjectRequest(
    val name: String? = null,
    val description: String? = null,
    val imageUrl: String? = null // empty string clears the image
)

@Serializable
data class ProjectMemberDto(
    val userId: String,
    val email: String,
    val name: String,
    val role: ProjectRole,
    val avatarUrl: String? = null,
    val avatarEmoji: String? = null
)

@Serializable
data class AddMemberRequest(
    val email: String,
    val role: ProjectRole
)

@Serializable
data class BoardDto(
    val id: String,
    val projectId: String,
    val name: String, // e.g. "Development", "Design", "Planning"
    val description: String = "",
    val position: Int = 0,
    val columns: List<BoardColumnDto> = emptyList(),
    val tasks: List<TaskDto> = emptyList()
)

/** A status column of a board. Tasks reference the column via [TaskDto.status]. */
@Serializable
data class BoardColumnDto(
    val id: String,
    val boardId: String,
    val name: String,
    val position: Int = 0,
    val color: String = "#5B68EF"
)

@Serializable
data class CreateColumnRequest(
    val name: String,
    val color: String = "#5B68EF"
)

@Serializable
data class UpdateColumnRequest(
    val name: String? = null,
    val color: String? = null,
    val position: Int? = null
)

@Serializable
data class CreateBoardRequest(
    val name: String,
    val description: String = "",
    val position: Int = 0
)

@Serializable
data class UpdateBoardRequest(
    val name: String? = null,
    val description: String? = null,
    val position: Int? = null
)

@Serializable
data class TaskDto(
    val id: String,
    val key: String, // e.g. "NIB-12"
    val boardId: String,
    val projectId: String,
    val title: String,
    val description: String = "",
    val type: IssueType = IssueType.TASK,
    val status: String = "", // id of the BoardColumnDto the task is in; blank = first column
    val priority: TaskPriority = TaskPriority.MEDIUM,
    val version: String? = null, // e.g. "v1.0.0"
    val sprintId: String? = null,
    val assignees: List<UserDto> = emptyList(),
    val commentCount: Int = 0,
    val creator: UserDto? = null,
    val createdAt: String,
    val updatedAt: String
)

@Serializable
data class CreateTaskRequest(
    val title: String,
    val description: String = "",
    val type: IssueType = IssueType.TASK,
    val status: String = "", // id of the BoardColumnDto the task is in; blank = first column
    val priority: TaskPriority = TaskPriority.MEDIUM,
    val version: String? = null,
    val sprintId: String? = null,
    val assigneeIds: List<String> = emptyList()
)

@Serializable
data class UpdateTaskRequest(
    val title: String? = null,
    val description: String? = null,
    val type: IssueType? = null,
    val status: String? = null,
    val priority: TaskPriority? = null,
    val version: String? = null,
    val sprintId: String? = null,
    /** Replaces the whole assignee list; null leaves it unchanged, an empty list unassigns everybody. */
    val assigneeIds: List<String>? = null,
    val boardId: String? = null
)

/** One entry of a task's activity log, e.g. "moved from TO DO to DONE". */
@Serializable
data class TaskLogDto(
    val id: String,
    val taskId: String,
    val user: UserDto? = null,
    val message: String,
    val createdAt: String
)

/** Moves an issue to [columnId] and puts it right before [beforeTaskId] (or at the end when that is null). */
@Serializable
data class MoveTaskRequest(
    val columnId: String,
    val beforeTaskId: String? = null
)

@Serializable
data class CommentDto(
    val id: String,
    val taskId: String,
    /** Null once the author's account has been deleted. */
    val author: UserDto? = null,
    val content: String,
    val createdAt: String
)

@Serializable
data class CreateCommentRequest(val content: String)

@Serializable
data class DocumentDto(
    val id: String,
    val projectId: String,
    val title: String,
    val content: String, // Markdown content
    val author: UserDto? = null,
    val createdAt: String,
    val updatedAt: String
)

@Serializable
data class CreateDocumentRequest(
    val title: String,
    val content: String = ""
)

@Serializable
data class UpdateDocumentRequest(
    val title: String? = null,
    val content: String? = null
)

@Serializable
data class ForgotPasswordRequest(val email: String)

@Serializable
data class ResetPasswordRequest(
    val token: String? = null,
    val email: String? = null,
    val code: String? = null,
    val newPassword: String
)

@Serializable
data class MessageResponse(val message: String)

@Serializable
data class ErrorResponse(
    val error: String,
    val message: String
)

